/**
 * capacityApi: transport + react-query hooks for the care-manager
 * capacity board and map.
 *
 *   GET /api/t/{slug}/v1/workspace/capacity?week=YYYY-MM-DD
 *   PUT /api/t/{slug}/v1/workspace/capacity/{rosterId}/availability
 *   GET /api/t/{slug}/v1/workspace/capacity/mentions?week=YYYY-MM-DD
 *   GET /api/t/{slug}/v1/workspace/capacity/hospice-and-deaths
 *   GET /api/t/{slug}/v1/map/clients
 *   GET /api/t/{slug}/v1/map/colors
 *   PUT /api/t/{slug}/v1/admin/map/colors
 *   PUT /api/t/{slug}/v1/admin/units/{id}/color
 *
 * House pattern: clientsApi.ts fetch semantics (raw fetch + ApiError
 * normalisation; TanStack Query owns caching). The board endpoints gate
 * on view_workspace / manage_staff_availability server-side; the client
 * pins gate on read_clinical_record and the care-team RLS; the map-colors
 * read gates on view_workspace (the same floor as the tiles — every
 * viewer of the map needs a legend that's right, not just clinical-read
 * holders) and its two writes on manage_team (api/src/functions/mapColors.ts).
 * Callers `enabled`-gate on the matching predicates so a denied role
 * fires no request.
 */

import { writeFetch } from './writeFetch';
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ApiError } from './api';
import { addDays, storedStance, type NewClientStance, type StatedAvailability } from './capacity';
import { CLIENT_MAP_PIN_COLOR, PEOPLE_MAP_PIN_COLOR, PLACEMENT_PIN_COLOR, PROVIDER_MAP_PIN_COLOR } from './mapColors';
import type { ClientEngagementValue } from './clientsApi';
import type { ClientNotPlacedReason } from './clientLocation';

export interface StaffAvailabilityWire {
  week_start: string;
  on_leave: boolean;
  /** The office's stance on a new client this week. */
  new_client_stance: NewClientStance;
  capacity_hours: string | null;
  notes: string | null;
  recorded_at: string;
  /** Incremented by every write (0216); sent back as expected_version.
   *  Absent reads as 0. */
  version?: number;
}

/** Someone has a row's week open (0216); null when nobody does. */
export interface EditLeaseWire {
  holder_name: string;
  mine?: boolean;
}

export interface CmCapacityRowWire {
  roster_id: string;
  full_name: string;
  unit_id: string | null;
  unit_name: string | null;
  unit_color: string | null;
  has_photo: boolean;
  target_weekly_hours: string | null;
  /** This week's hours (Mon through Sun of the selected week): imported
   *  CareTree time plus the hours of every note the person posted,
   *  counted when the note posts rather than when it is marked Ready.
   *  A demo client's hours are left out of both. The merged availability
   *  table labels this "This week so far". */
  hours_worked: string;
  /** The week immediately before the selected one, same shape as
   *  hours_worked: the merged availability table's "Last week" column. */
  hours_worked_last_week: string;
  /** Active clients, not demo clients, where this care manager is the
   *  lead or an active additional care manager, each client once. */
  active_client_count: number;
  /** The home point, null when none is on file. Absent (with home_city
   *  and home_precision) for a seat that does not assign: the server
   *  leaves the keys out, and absent never means "none on file". */
  home?: { lat: number; lng: number } | null;
  home_city?: string | null;
  /** How the home point was placed: 'zip' marks an approximate pin. */
  home_precision?: 'street' | 'zip' | 'none' | null;
  unit_counties: string[];
  /** The week's override, null when none was recorded. Present, with
   *  new_client_stance, for a seat that sees the new-client stance; both
   *  are absent otherwise. */
  availability?: StaffAvailabilityWire | null;
  /** Who has this row's week open (0216). */
  edit_lease?: EditLeaseWire | null;
  /** The stance the server decided from the override: Open, Talk to Me,
   *  Not Open or On leave; a person with no override is Open. Sent
   *  exactly when availability is. */
  new_client_stance?: StatedAvailability;
}

/** A SPICE therapist's row on the capacity wire: the same hours, target,
 *  caseload and availability facts a care-manager row carries (the
 *  merged availability table reads only those, in hours), plus the
 *  schedule's planned-session figures that "place a client" reads:
 *  planned_sessions, target_weekly_hours (as sessions, one hour each)
 *  and open_weekdays. Nothing here is scored from notes; the SPICE
 *  schedule grid reads outcomes through its own route. */
export interface SpiceCapacityRowWire {
  roster_id: string;
  full_name: string;
  unit_id: string | null;
  unit_name: string | null;
  unit_color: string | null;
  has_photo: boolean;
  /** staff_roster.target_weekly_hours; null = no target set. The one
   *  target, same as a care manager's; "place a client" reads it as
   *  sessions at one hour each. */
  target_weekly_hours: string | null;
  /** This week's hours — see CmCapacityRowWire.hours_worked. */
  hours_worked: string;
  /** Last week's hours — see CmCapacityRowWire.hours_worked_last_week. */
  hours_worked_last_week: string;
  /** Active clients, not demo clients, where this therapist is the lead or
   *  an active additional SPICE therapist, each client once. */
  active_client_count: number;
  /** Sessions not on hold this week, Monday to Friday, on clients that are
   *  not demo clients. */
  planned_sessions: number;
  /** The hours target read as sessions at one hour each, minus planned;
   *  null when there is no target. */
  open_sessions: number | null;
  /** Weekdays (1..5) with fewer than two planned slots; empty when there
   *  is no target set (nothing to compare against yet). */
  open_weekdays: number[];
  /** The week's availability override (the stance on new clients, on
   *  leave, a note), the same per-week row a care manager's carries; it
   *  is keyed by the roster id, not the role. Present, with
   *  new_client_stance, for a seat that sees the new-client stance; both
   *  are absent otherwise. */
  availability?: StaffAvailabilityWire | null;
  /** Who has this row's week open (0216). */
  edit_lease?: EditLeaseWire | null;
  /** The stance the server decided from the override; sent exactly when
   *  availability is. */
  new_client_stance?: StatedAvailability;
  /** As the care manager row's: absent for a seat that does not assign. */
  home?: { lat: number; lng: number } | null;
  home_precision?: 'street' | 'zip' | 'none' | null;
}

/**
 * One row of the merged availability table (Team > Availability): a
 * care manager or a SPICE therapist normalised to the cells they share,
 * all of them in hours. kind decides only which name links to the
 * care-manager view and where the row groups (SPICE as its own trailing
 * group). Built client-side from careManagers/spiceTherapists — the wire
 * keeps its own two arrays (the map panel and "place a client" read them
 * independently), this is a presentation-only merge.
 */
export interface AvailabilityRowWire {
  kind: 'care_manager' | 'spice_therapist';
  roster_id: string;
  full_name: string;
  /** The unit the row groups under; the group header names it, the row
   *  itself carries no unit cell. */
  unit_name: string | null;
  has_photo: boolean;
  /** The one target, in hours, for every row kind (weeklyTargetHours). */
  target_weekly_hours: string | null;
  hours_worked: string;
  hours_worked_last_week: string;
  active_client_count: number;
  /** The week's override and the server's stance, copied only when the
   *  server sent them; a row without them shows no stance and offers no
   *  write. */
  availability?: StaffAvailabilityWire | null;
  /** Who has this row's week open (0216). */
  edit_lease?: EditLeaseWire | null;
  new_client_stance?: StatedAvailability;
  /** The unit group header's counties line; [] for a SPICE row (SPICE
   *  always renders as its own single group, never split by county). */
  unit_counties: string[];
}

/** The stance keys a wire row carries, copied only when present: a key
 *  the server left out stays absent, never a present undefined. */
function stanceKeysOf(
  r: Pick<CmCapacityRowWire, 'availability' | 'new_client_stance' | 'edit_lease'>,
): Pick<AvailabilityRowWire, 'availability' | 'new_client_stance' | 'edit_lease'> {
  return {
    ...(r.availability !== undefined ? { availability: r.availability } : {}),
    ...(r.new_client_stance !== undefined ? { new_client_stance: r.new_client_stance } : {}),
    ...(r.edit_lease !== undefined ? { edit_lease: r.edit_lease } : {}),
  };
}

/** Normalises the board's two arrays into one, tagged by kind, for the
 *  merged availability table. Order: every care manager, then every
 *  SPICE therapist — the caller groups from there (SPICE renders as its
 *  own group regardless of any unit_name on the row). The stance keys
 *  are copied only when the server sent them. */
export function toAvailabilityRows(
  careManagers: readonly CmCapacityRowWire[],
  spiceTherapists: readonly SpiceCapacityRowWire[],
): AvailabilityRowWire[] {
  const cmRows: AvailabilityRowWire[] = careManagers.map((r) => ({
    kind: 'care_manager',
    roster_id: r.roster_id,
    full_name: r.full_name,
    unit_name: r.unit_name,
    has_photo: r.has_photo,
    target_weekly_hours: r.target_weekly_hours,
    hours_worked: r.hours_worked,
    hours_worked_last_week: r.hours_worked_last_week,
    active_client_count: r.active_client_count,
    ...stanceKeysOf(r),
    unit_counties: r.unit_counties,
  }));
  const spiceRows: AvailabilityRowWire[] = spiceTherapists.map((r) => ({
    kind: 'spice_therapist',
    roster_id: r.roster_id,
    full_name: r.full_name,
    unit_name: r.unit_name,
    has_photo: r.has_photo,
    target_weekly_hours: r.target_weekly_hours,
    hours_worked: r.hours_worked,
    hours_worked_last_week: r.hours_worked_last_week,
    active_client_count: r.active_client_count,
    ...stanceKeysOf(r),
    unit_counties: [],
  }));
  return [...cmRows, ...spiceRows];
}

export interface CmCapacityWeekWire {
  week_start: string;
  week_end: string;
  hours_source: 'imported_time_and_posted_notes';
  internal_time_included: false;
  care_managers: CmCapacityRowWire[];
  spice_therapists: SpiceCapacityRowWire[];
  request_id?: string;
}

export interface StaffAvailabilityInput {
  week_start: string;
  on_leave: boolean;
  new_client_stance: NewClientStance;
  capacity_hours: number | null;
  notes: string | null;
  /** The version the writer last saw (0216); null = no row for the week.
   *  A save over a newer change is refused with 409 availability_changed. */
  expected_version: number | null;
  /** The clients named on this week's call, the whole set (0243). Absent
   *  leaves the stored mentions as they are. */
  mentions?: ClientCallMentionInput[];
}

/** The weekly call's three tags (client_call_mentions_tag_check). */
export type ClientCallMentionTag = 'hospice' | 'passed_away' | 'other';

/** One client named on the call. hospice carries the day the client went
 *  on hospice, passed_away the date of death; other carries no date. */
export interface ClientCallMentionInput {
  client_id: string;
  tag: ClientCallMentionTag;
  date: string | null;
}

export interface CallMentionWire {
  roster_id: string;
  client_id: string;
  display_name: string;
  tag: ClientCallMentionTag;
  /** The record's, at read time. */
  on_hospice_since: string | null;
  date_of_death: string | null;
}

export interface RecordStatusClientWire {
  client_id: string;
  display_name: string;
  /** The hospice day or the date of death; null = died, no date recorded. */
  date: string | null;
  cm_roster_id: string | null;
  cm_name: string | null;
}

export interface HospiceAndDeathsWire {
  on_hospice: RecordStatusClientWire[];
  passed_away: RecordStatusClientWire[];
  passed_away_since: string;
}

export interface ClientMapPinWire {
  client_id: string;
  display_name: string;
  /** The client's current location: the "currently at" address, else
   *  the primary. */
  lat: number;
  lng: number;
  /** 'zip' when only the town or ZIP was found: an approximate pin. */
  precision: 'street' | 'zip';
  /** The lead care manager; cm_name and the unit are hers. */
  cm_roster_id: string | null;
  /** Every care manager on the client: the lead first, then each active
   *  additional care manager by name. Empty when the client has none. */
  cm_roster_ids: string[];
  /** The viewer's own client (on her caseload by any arm); "My clients" draws only these. */
  mine?: boolean;
  cm_name: string | null;
  unit_id: string | null;
  unit_name: string | null;
  unit_color: string | null;
  /** The county of the client's HOME address (the primary), wherever
   *  they are staying now. */
  county_name: string | null;
  /** SPICE Train A2: "place a client" in SPICE mode needs the CURRENT
   *  engagement to compute the transition (care_management -> spice;
   *  both stays both) when assigning a therapist. */
  engagement: ClientEngagementValue;
}

export interface ClientMapNotPlacedWire {
  client_id: string;
  display_name: string;
  /** The lead care manager. */
  cm_roster_id: string | null;
  /** Every care manager on the client, lead first, as on a pin. */
  cm_roster_ids: string[];
  /** The viewer's own client (on her caseload by any arm); "My clients" draws only these. */
  mine?: boolean;
  /** Why the client is not on the map (CLIENT_NOT_PLACED_LABEL); no_address
   *  for a client with no active address at all (LP-42). */
  reason: ClientNotPlacedReason;
}

export interface ClientMapWire {
  pins: ClientMapPinWire[];
  not_placed: ClientMapNotPlacedWire[];
  no_address_count: number | null;
  request_id?: string;
}

function base(slug: string): string {
  return `/api/t/${encodeURIComponent(slug)}/v1`;
}

async function parseOrThrow<T>(res: Response, label: string): Promise<T> {
  if (!res.ok) {
    let body: unknown;
    try {
      body = await res.json();
    } catch {
      body = await res.text().catch(() => null);
    }
    throw new ApiError(res.status, body, `${label} → HTTP ${res.status}`);
  }
  return (await res.json()) as T;
}

export async function fetchCmCapacity(slug: string, week: string, signal?: AbortSignal): Promise<CmCapacityWeekWire> {
  const url = `${base(slug)}/workspace/capacity?week=${encodeURIComponent(week)}`;
  const res = await fetch(url, { method: 'GET', headers: { Accept: 'application/json' }, signal });
  return parseOrThrow<CmCapacityWeekWire>(res, `GET ${url}`);
}

export async function fetchCallMentions(
  slug: string,
  week: string,
  signal?: AbortSignal,
): Promise<{ week_start: string; mentions: CallMentionWire[] }> {
  const url = `${base(slug)}/workspace/capacity/mentions?week=${encodeURIComponent(week)}`;
  const res = await fetch(url, { method: 'GET', headers: { Accept: 'application/json' }, signal });
  return parseOrThrow<{ week_start: string; mentions: CallMentionWire[] }>(res, `GET ${url}`);
}

export async function fetchHospiceAndDeaths(slug: string, signal?: AbortSignal): Promise<HospiceAndDeathsWire> {
  const url = `${base(slug)}/workspace/capacity/hospice-and-deaths`;
  const res = await fetch(url, { method: 'GET', headers: { Accept: 'application/json' }, signal });
  return parseOrThrow<HospiceAndDeathsWire>(res, `GET ${url}`);
}

export async function putStaffAvailability(
  slug: string,
  rosterId: string,
  input: StaffAvailabilityInput,
): Promise<{ availability: StaffAvailabilityWire }> {
  const url = `${base(slug)}/workspace/capacity/${encodeURIComponent(rosterId)}/availability`;
  const res = await writeFetch(url, {
    method: 'PUT',
    headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
    body: JSON.stringify(input),
  });
  return parseOrThrow<{ availability: StaffAvailabilityWire }>(res, `PUT ${url}`);
}

/** Why putNewClientStance wrote nothing: the board it read carried no
 *  override for the row. The office's own save message is what a person
 *  sees. */
const STANCE_NOT_SENT = 'The availability board was read without the stored override, so nothing was saved.';

/**
 * The board's "New clients" toggle: writes the chosen stance through the
 * existing per-week override route, carrying the row's other override
 * fields (on_leave, capacity_hours, notes) through untouched.
 *
 * A save sets the chosen stance for this week and the following week
 * together, unless the following week was set on purpose in a separate
 * step (the week picker, on that week). No row records which way a week
 * was set, so it is read from the stored stances: the following week was
 * set on purpose when its stored stance differs from this week's stored
 * stance before this save. So:
 *   - the following week has no row: it gets this week's body under its
 *     own date, this week's on leave, hours and note with it;
 *   - its stored stance equals this week's: it gets the new stance, with
 *     its own on leave, hours and note;
 *   - its stored stance differs: it is left alone.
 * The following week is read with the same GET the week picker uses.
 *
 * A row the server sent without its override cannot be carried through,
 * so nothing is written: both weeks are read before the first PUT, and a
 * missing override on either refuses the write.
 */
export async function putNewClientStance(
  slug: string,
  row: Pick<AvailabilityRowWire, 'roster_id' | 'availability' | 'new_client_stance'>,
  weekStart: string,
  stance: NewClientStance,
): Promise<{ availability: StaffAvailabilityWire }> {
  const current = row.availability;
  const before = storedStance(row);
  if (current === undefined || before === undefined) throw new Error(STANCE_NOT_SENT);

  const nextWeek = addDays(weekStart, 7);
  const nextBoard = await fetchCmCapacity(slug, nextWeek);
  const nextRow = [...nextBoard.care_managers, ...nextBoard.spice_therapists].find(
    (r) => r.roster_id === row.roster_id,
  );
  if (nextRow !== undefined && nextRow.availability === undefined) throw new Error(STANCE_NOT_SENT);

  // Each week names the version it was read at (0216), so a toggle on a
  // board loaded before someone else saved cannot write their change back.
  // A refusal of this week throws before the following week is written.
  // The two weeks are two requests: when this week saves and the following
  // week is refused, this week's change stands, and the refusal reaches the
  // caller, which refreshes the board.
  const input: StaffAvailabilityInput = {
    week_start: weekStart,
    on_leave: current?.on_leave ?? false,
    new_client_stance: stance,
    capacity_hours: hoursOf(current),
    notes: current?.notes ?? null,
    expected_version: current === null ? null : (current.version ?? 0),
  };
  const result = await putStaffAvailability(slug, row.roster_id, input);

  const next = nextRow?.availability ?? null;
  if (next === null) {
    await putStaffAvailability(slug, row.roster_id, { ...input, week_start: nextWeek, expected_version: null });
  } else if (next.new_client_stance === before) {
    await putStaffAvailability(slug, row.roster_id, {
      week_start: nextWeek,
      on_leave: next.on_leave,
      new_client_stance: stance,
      capacity_hours: hoursOf(next),
      notes: next.notes,
      expected_version: next.version ?? 0,
    });
  }

  return result;
}

/** A stored override's hours as the PUT takes them. */
function hoursOf(a: StaffAvailabilityWire | null): number | null {
  return a?.capacity_hours == null ? null : Number(a.capacity_hours);
}

export async function fetchClientMap(slug: string, signal?: AbortSignal): Promise<ClientMapWire> {
  const url = `${base(slug)}/map/clients`;
  const res = await fetch(url, { method: 'GET', headers: { Accept: 'application/json' }, signal });
  return parseOrThrow<ClientMapWire>(res, `GET ${url}`);
}

/** Why a typed address was not placed (the server's notPlacedReasonFor). */
export type MapGeocodeNotPlacedReason = 'no_street_address' | 'not_found_add_state' | 'not_found';

export interface MapGeocodeWire {
  lat: number | null;
  lng: number | null;
  precision: 'street' | 'zip' | 'none';
  county_name: string | null;
  /** Why the address was not placed; null when it was. */
  not_placed_reason?: MapGeocodeNotPlacedReason | null;
  request_id?: string;
}

/** POST /map/geocode: "place a client"'s typed-address mode. The address
 *  is sent once and never stored server-side. */
export async function postMapGeocode(slug: string, address: string): Promise<MapGeocodeWire> {
  const url = `${base(slug)}/map/geocode`;
  const res = await writeFetch(url, {
    method: 'POST',
    headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
    body: JSON.stringify({ address }),
  });
  return parseOrThrow<MapGeocodeWire>(res, `POST ${url}`);
}

/**
 * POST /workspace/assignment-suggestions: for an address or a bare ZIP,
 * or for a client (whose home address decides, with the card's ZIP to
 * fall back on), the covering unit and every care manager with a home
 * point in two blocks, available (Open and Talk to Me) then not, nearest
 * first inside each. Nothing in it is computed from hours; the assigner
 * decides.
 */
export interface SuggestedCareManagerWire {
  roster_id: string;
  full_name: string;
  unit_id: string | null;
  unit_name: string | null;
  unit_color: string | null;
  /** Absent for a seat that does not assign: the order stands, the
   *  distance is not shown. */
  distance_miles?: number;
  /** The stance the server decided: Open, Talk to Me, Not Open or On leave. */
  stated_availability: StatedAvailability;
  hours_worked_last_week: string;
  target_weekly_hours: string | null;
  covers_county: boolean;
}

export interface AssignmentSuggestionsWire {
  /** null when the address could not be placed; absent for a seat that
   *  does not assign (the address was placed, the point is not sent). */
  point?: { lat: number; lng: number; precision: 'street' | 'zip' | 'none' } | null;
  county_name: string | null;
  covering_units: { unit_id: string; unit_name: string }[];
  available: SuggestedCareManagerWire[];
  unavailable: SuggestedCareManagerWire[];
  skipped_without_home: number;
  /** What the ranking was read from: the client's home address, the
   *  address or ZIP sent, or nothing to place (null). */
  basis?: 'home_address' | 'address' | null;
}

/** The route's two request shapes. */
export type AssignmentSuggestionsRequest =
  | { address: string }
  | { client_id: string; fallback_address: string | null };

export async function postAssignmentSuggestions(
  slug: string,
  request: AssignmentSuggestionsRequest,
): Promise<AssignmentSuggestionsWire> {
  const url = `${base(slug)}/workspace/assignment-suggestions`;
  const res = await writeFetch(url, {
    method: 'POST',
    headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
    body: JSON.stringify(request),
  });
  return parseOrThrow<AssignmentSuggestionsWire>(res, `POST ${url}`);
}

/** Every suggestion cached for the tenant; an address save invalidates it. */
export function assignmentSuggestionsPrefix(slug: string): readonly unknown[] {
  return ['assignment-suggestions', slug] as const;
}

export function assignmentSuggestionsKey(slug: string, zip: string, clientId: string | null = null): readonly unknown[] {
  return ['assignment-suggestions', slug, clientId, zip] as const;
}

/** Read-shaped despite the POST (the body carries free text); cached per
 *  client and ZIP for a minute so reopening the same dialog does not
 *  geocode twice. With a client the route decides from the client's home
 *  address and falls back to the ZIP; without one it asks about the ZIP. */
export function useAssignmentSuggestions(slug: string, target: { clientId: string | null; zip: string | null }) {
  const zip = target.zip?.trim() ?? '';
  const clientId = target.clientId ?? null;
  return useQuery<AssignmentSuggestionsWire>({
    queryKey: assignmentSuggestionsKey(slug, zip, clientId),
    queryFn: () =>
      postAssignmentSuggestions(
        slug,
        clientId !== null ? { client_id: clientId, fallback_address: zip === '' ? null : zip } : { address: zip },
      ),
    enabled: slug.length > 0 && (clientId !== null || zip.length > 0),
    staleTime: 60_000,
    retry: false,
  });
}

/** Why an open intake card cannot be pinned yet (GET /map/prospects);
 *  the plain words are CLIENT_NOT_PLACED_LABEL (src/lib/clientLocation.ts). */
export type MapProspectNotPlaceableReason = ClientNotPlacedReason;

/** One open intake card with a client row. Every such card is listed so
 *  it can be found by name; `placeable` says whether it can be pinned
 *  (lat/lng set), and when it cannot, `not_placeable_reason` says why. */
export interface MapProspectWire {
  work_item_id: string;
  client_id: string;
  display_name: string;
  /** The intake stage's label ("Ready for Assignment"); null without a stage. */
  stage_label: string | null;
  placeable: boolean;
  not_placeable_reason: MapProspectNotPlaceableReason | null;
  /** The client's current location, when it is placed. */
  lat: number | null;
  lng: number | null;
  /** 'zip' when only the town or ZIP was found; null when not placeable. */
  precision?: 'street' | 'zip' | null;
  assignee_roster_id: string | null;
  /** The county of the client's HOME address, placeable or not. */
  county_name: string | null;
  /** The current location row's town and state, trimmed, placed or not;
   *  null with no current location or an empty value. Never the home
   *  row's unless the home row is the current location. */
  location_city: string | null;
  location_state: string | null;
  /** Same reason as ClientMapPinWire.engagement: the record's current
   *  engagement rides along for any assign that needs it. */
  engagement: ClientEngagementValue;
}

export async function fetchMapProspects(slug: string, signal?: AbortSignal): Promise<{ prospects: MapProspectWire[] }> {
  const url = `${base(slug)}/map/prospects`;
  const res = await fetch(url, { method: 'GET', headers: { Accept: 'application/json' }, signal });
  return parseOrThrow<{ prospects: MapProspectWire[] }>(res, `GET ${url}`);
}

/** One active unit's map color, as GET /map/colors and the Admin > Units
 *  "Map colours" picker both see it — `color` is the RAW units.color
 *  value (null when unset), not a resolved fallback, so the picker can
 *  show "not set, using the palette default" accurately. */
export interface UnitMapColorWire {
  unit_id: string;
  unit_name: string;
  color: string | null;
}

/** GET /map/colors — every active unit's color plus the tenant's four
 *  configurable colors, ALWAYS resolved (never null): a tenant that has
 *  never saved sees the same brand-adjacent defaults the server falls
 *  back to (src/lib/mapColors.ts DEFAULT_MAP_COLORS). */
export interface MapColorsWire {
  units: UnitMapColorWire[];
  clients: string;
  prospects: string;
  placement_pin: string;
  providers: string;
  people: string;
  request_id?: string;
}

export async function fetchMapColors(slug: string, signal?: AbortSignal): Promise<MapColorsWire> {
  const url = `${base(slug)}/map/colors`;
  const res = await fetch(url, { method: 'GET', headers: { Accept: 'application/json' }, signal });
  return parseOrThrow<MapColorsWire>(res, `GET ${url}`);
}

export interface SetMapColorsInput {
  clients: string;
  prospects: string;
  placement_pin: string;
  providers: string;
  people: string;
}

/** PUT /admin/map/colors — replaces the tenant's whole five-color set. */
export async function putMapColors(slug: string, input: SetMapColorsInput): Promise<Omit<MapColorsWire, 'units'>> {
  const url = `${base(slug)}/admin/map/colors`;
  const res = await writeFetch(url, {
    method: 'PUT',
    headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
    body: JSON.stringify(input),
  });
  return parseOrThrow<Omit<MapColorsWire, 'units'>>(res, `PUT ${url}`);
}

/** PUT /admin/units/{id}/color — one unit's color. `color: null` clears
 *  it back to the deterministic palette fallback (unitDotColor). */
export async function putUnitColor(slug: string, unitId: string, color: string | null): Promise<UnitMapColorWire> {
  const url = `${base(slug)}/admin/units/${encodeURIComponent(unitId)}/color`;
  const res = await writeFetch(url, {
    method: 'PUT',
    headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
    body: JSON.stringify({ color }),
  });
  return parseOrThrow<UnitMapColorWire>(res, `PUT ${url}`);
}

export function mapColorsKey(slug: string): readonly unknown[] {
  return ['map-colors', slug] as const;
}

export function useMapColors(slug: string, opts: { enabled?: boolean } = {}) {
  const query = useQuery<MapColorsWire>({
    queryKey: mapColorsKey(slug),
    queryFn: ({ signal }) => fetchMapColors(slug, signal),
    staleTime: 60_000,
    enabled: (opts.enabled ?? true) && slug !== '',
  });
  return {
    units: query.data?.units ?? [],
    clients: query.data?.clients ?? CLIENT_MAP_PIN_COLOR,
    prospects: query.data?.prospects ?? CLIENT_MAP_PIN_COLOR,
    placement_pin: query.data?.placement_pin ?? PLACEMENT_PIN_COLOR,
    providers: query.data?.providers ?? PROVIDER_MAP_PIN_COLOR,
    people: query.data?.people ?? PEOPLE_MAP_PIN_COLOR,
    isLoading: query.isLoading,
    isError: query.isError,
    error: query.error,
    refetch: () => {
      void query.refetch();
    },
  };
}

export function useSetMapColors(slug: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: SetMapColorsInput) => putMapColors(slug, input),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: mapColorsKey(slug) });
    },
  });
}

export function useSetUnitColor(slug: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (vars: { unitId: string; color: string | null }) => putUnitColor(slug, vars.unitId, vars.color),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: mapColorsKey(slug) });
      // The board/map's own CM rows carry unit_color too (cmCapacity),
      // so a color save should refresh whatever week is on screen.
      void qc.invalidateQueries({ queryKey: ['cm-capacity', slug] });
    },
  });
}

/** The tile URL template the map hands to Leaflet ({z}/{x}/{y} literal). */
export function tileUrlTemplate(slug: string): string {
  return `${base(slug)}/map/tiles/{z}/{x}/{y}`;
}

export function cmCapacityKey(slug: string, week: string): readonly unknown[] {
  return ['cm-capacity', slug, week] as const;
}

export function clientMapKey(slug: string): readonly unknown[] {
  return ['client-map', slug] as const;
}

/** `keepPreviousWeek`: while a new week loads, the previous week's rows
 *  stay as the data (the Map tab, so the map is not rebuilt at midnight on
 *  Sunday). Off, a new week starts empty. */
export function useCmCapacity(slug: string, week: string, opts: { enabled?: boolean; keepPreviousWeek?: boolean } = {}) {
  const query = useQuery<CmCapacityWeekWire>({
    queryKey: cmCapacityKey(slug, week),
    queryFn: ({ signal }) => fetchCmCapacity(slug, week, signal),
    staleTime: 30_000,
    enabled: (opts.enabled ?? true) && slug !== '' && week !== '',
    placeholderData: opts.keepPreviousWeek ? keepPreviousData : undefined,
  });
  return {
    data: query.data ?? null,
    careManagers: query.data?.care_managers ?? [],
    spiceTherapists: query.data?.spice_therapists ?? [],
    isLoading: query.isLoading,
    isError: query.isError,
    error: query.error,
    refetch: () => {
      void query.refetch();
    },
  };
}

export function useSetStaffAvailability(slug: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (vars: { rosterId: string; input: StaffAvailabilityInput }) =>
      putStaffAvailability(slug, vars.rosterId, vars.input),
    onSuccess: (_d, vars) => {
      void qc.invalidateQueries({ queryKey: ['cm-capacity', slug] });
      // A save that named clients may have set a hospice day or a date of
      // death on the record.
      if (vars.input.mentions !== undefined) {
        void qc.invalidateQueries({ queryKey: ['call-mentions', slug] });
        void qc.invalidateQueries({ queryKey: hospiceAndDeathsKey(slug) });
        void qc.invalidateQueries({ queryKey: ['clientRecord', slug] });
      }
    },
  });
}

export function callMentionsKey(slug: string, week: string): readonly unknown[] {
  return ['call-mentions', slug, week] as const;
}

export function hospiceAndDeathsKey(slug: string): readonly unknown[] {
  return ['hospice-and-deaths', slug] as const;
}

/** The clients named on the week's calls, every care manager's. */
export function useCallMentions(slug: string, week: string, opts: { enabled?: boolean } = {}) {
  const query = useQuery<{ week_start: string; mentions: CallMentionWire[] }>({
    queryKey: callMentionsKey(slug, week),
    queryFn: ({ signal }) => fetchCallMentions(slug, week, signal),
    staleTime: 30_000,
    enabled: (opts.enabled ?? true) && slug !== '' && week !== '',
  });
  const mentions = Array.isArray(query.data?.mentions) ? query.data.mentions : [];
  return { mentions, isLoading: query.isLoading, isError: query.isError };
}

/** The company's clients on hospice and those who died in the last eight
 *  weeks, from the record. */
export function useHospiceAndDeaths(slug: string, opts: { enabled?: boolean } = {}) {
  const query = useQuery<HospiceAndDeathsWire>({
    queryKey: hospiceAndDeathsKey(slug),
    queryFn: ({ signal }) => fetchHospiceAndDeaths(slug, signal),
    staleTime: 30_000,
    enabled: (opts.enabled ?? true) && slug !== '',
  });
  // A body without both lists is no band, never a broken one.
  const d = query.data;
  const data = d && Array.isArray(d.on_hospice) && Array.isArray(d.passed_away) ? d : null;
  return { data, isLoading: query.isLoading, isError: query.isError, error: query.error };
}

/** The board's stance toggle. Its onSuccess returns the refetch, so a
 *  caller's own onSuccess runs only once the board has answered: the next
 *  save always compares against the stored stance. */
export function useSetNewClientStance(slug: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (vars: {
      row: Pick<AvailabilityRowWire, 'roster_id' | 'availability' | 'new_client_stance'>;
      weekStart: string;
      stance: NewClientStance;
    }) => putNewClientStance(slug, vars.row, vars.weekStart, vars.stance),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['cm-capacity', slug] }),
  });
}

export function useClientMap(slug: string, opts: { enabled?: boolean } = {}) {
  const query = useQuery<ClientMapWire>({
    queryKey: clientMapKey(slug),
    queryFn: ({ signal }) => fetchClientMap(slug, signal),
    staleTime: 60_000,
    enabled: (opts.enabled ?? true) && slug !== '',
  });
  return {
    data: query.data ?? null,
    isLoading: query.isLoading,
    isError: query.isError,
    error: query.error,
    refetch: () => {
      void query.refetch();
    },
  };
}

/** "Place a client"'s typed-address mode: a mutation, not a query — every
 *  call is a deliberate "locate this" action, never a background refetch. */
export function useMapGeocode(slug: string) {
  return useMutation({
    mutationFn: (address: string) => postMapGeocode(slug, address),
  });
}

export function mapProspectsKey(slug: string): readonly unknown[] {
  return ['map-prospects', slug] as const;
}

/** Lazy like ClientLookupField's client list: the prospect list loads
 *  once the picker opens, not on every map render. The intake card's
 *  Location line reads the same list under the same key, so one read
 *  serves the drawer and the map. */
export function useMapProspects(slug: string, opts: { enabled?: boolean } = {}) {
  const query = useQuery<{ prospects: MapProspectWire[] }>({
    queryKey: mapProspectsKey(slug),
    queryFn: ({ signal }) => fetchMapProspects(slug, signal),
    staleTime: 60_000,
    enabled: (opts.enabled ?? true) && slug !== '',
  });
  return {
    prospects: query.data?.prospects ?? [],
    isLoading: query.isLoading,
    isError: query.isError,
    isSuccess: query.isSuccess,
  };
}

// ---------------------------------------------------------------------
// The edit lease (0216): who has one care manager's week open.
//   POST   .../workspace/capacity/{rosterId}/availability/lease?week=
//          take or renew -> { lease: { mine, holder_name?, expires_at } }
//   DELETE same -> 204, the caller's own lease only
// ---------------------------------------------------------------------

export type AvailabilityLeaseWire =
  | { mine: true; expires_at: string }
  | { mine: false; holder_name: string; expires_at: string };

function leasePath(slug: string, rosterId: string, weekStart: string): string {
  return `${base(slug)}/workspace/capacity/${encodeURIComponent(rosterId)}/availability/lease?week=${encodeURIComponent(weekStart)}`;
}

export async function takeAvailabilityLease(slug: string, rosterId: string, weekStart: string): Promise<AvailabilityLeaseWire> {
  const path = leasePath(slug, rosterId, weekStart);
  const res = await writeFetch(path, { method: 'POST', headers: { Accept: 'application/json' }, redirect: 'manual' });
  return (await parseOrThrow<{ lease: AvailabilityLeaseWire }>(res, `POST ${path}`)).lease;
}

/** Best-effort: keepalive so a closing tab still sends it. A lease that
 *  is never released lapses on its own. */
export function releaseAvailabilityLease(slug: string, rosterId: string, weekStart: string): void {
  void writeFetch(leasePath(slug, rosterId, weekStart), { method: 'DELETE', keepalive: true }).catch(() => undefined);
}
